import { eCommercePlatform } from '@/model/Constants';
import fflSelectInterface from '@/model/ffl-select.mjs';
import { DealerActionTimeout, UseCreateStoreFormInterface } from '@/model/profile.mjs';
import StoreDetailsInterface, { TrackingInterface } from '@/model/store.mjs';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { NavigateFunction } from 'react-router';

interface AppState {
  isStoreAdmin: boolean;
  isStoreAdminRole: boolean;
  isGlobalAdmin: boolean;
  isGlobalOperationsAdmin: boolean;
  isPreferredReportsRole: boolean;
  isSearchOnlyRole: boolean;
  currentUserGroup: string;
  isGlobalAdminRole: boolean;
  isGlobalOperationsAdminRole: boolean;
  storeId: string;
  userName: string;
  fflSelectAPIKey: fflSelectInterface;
  dealerActionTimeout: DealerActionTimeout; 
  storeRefresh: boolean;
  storeData: StoreDetailsInterface;
  createStoreData: StoreDetailsInterface;
  isCreateStoreLoading: boolean;
  emailExistError: string;
  domainExistError: string;
  storeLicenseExistError: string;
  isImpersonating: boolean;
  isUserInfoLoaded: boolean;
  isMyFFLRole: boolean;
  myFFLDealerId: string;
  isProcessCompleted: boolean;
  refreshStatus: boolean;
  isStoreAgent: boolean;
  userId: string;
  storeList:any;
  userEmail: string;
  storeDealerId?: string;
  userPermissions?: any
  trackingData?: TrackingInterface;
  isAddDealerEventLoading: boolean;
  addDealerEventSuccess: boolean;
}

export interface AddDealerEventPayload {
  license_key?: string;
  transfer_id?: string;
  order_number?: string;
  store_id?: string;
  action: string;
  notes: string;
}
export const fflSelectInitialValue = {
  api_key: "",
  created_at: "",
  created_by: "",
  id: 0,
  store_id: "",
  updated_at: "",
  updated_by: "",
  encryption_key: "",
  ffl_transfer_api_url: "",
  ffl_select_url: "",
  google_map_key: "",
  master_proof_reciever_webhook:"",
  store_configuration_webhook: "",
  agent_email: "",
  e_commerce_platform: eCommercePlatform.MAGENTO,
  allow_preferred_license_upgrade: false,
}
export const dealerActionTimeout = {
  dealer_unresponsive_initiation : 0,
  dealer_unresponsive_confirmation :  0,
  dealer_sms_email_update_timeout: 0,
  ffl_support_email_update_timeout : 0,
  store_agent_email_update_timeout :  0,
  customer_action_timeout: 0,
  dealer_last_chance_action_timeout: 0,
  dealer_documents_processing_timeout : 0,
  dealer_documents_reupload_timeout :  0,  
  customer_reassignment_email_reminder_timeout : 0,
  transfer_sms_send_to_dealer_timeout: 0
}
export const storeDataInitialValue: StoreDetailsInterface = {
  access_credentials: null,
  contact_email: null,
  contact_phone: null,
  name: '',
  seller_email: null,
  status: null,
  store_id: '',
  subscription_end_date: '',
  subscription_start_date: null,
  website: null,
  store_lic_type: null,
  store_license_file_path: null,
  store_license_key: null,
  store_premise_city: null,
  store_premise_state: null,
  store_contact_fax: null,
  store_premise_street: null,
  store_premise_zip_code: null,
  dealer_id: null,
  bcc_email: '',
};
const initialState: AppState = {
  isStoreAdmin: false,
  isStoreAdminRole: false,
  isGlobalAdmin: false,
  isGlobalOperationsAdmin: false,
  isGlobalAdminRole: false,
  isPreferredReportsRole: false,
  isSearchOnlyRole: false,
  currentUserGroup: '',
  isGlobalOperationsAdminRole:false,
  storeId: '',
  userName: '',
  fflSelectAPIKey: fflSelectInitialValue,
  dealerActionTimeout: dealerActionTimeout,
  storeRefresh: false,
  storeData: storeDataInitialValue,
  createStoreData: storeDataInitialValue,
  isCreateStoreLoading: false,
  emailExistError: '',
  domainExistError: '',
  storeLicenseExistError: '',
  isImpersonating: false,
  isUserInfoLoaded: false,
  isMyFFLRole: false,
  myFFLDealerId: '',
  isProcessCompleted:false,
  refreshStatus:false,
  isStoreAgent:false,
  userId: "",
  storeList: [],
  userEmail: "",
  storeDealerId: "",
  userPermissions: {},
  trackingData:{
    type: '',
    time: new Date(),
    email: '',
    storeId: ''
  },
  isAddDealerEventLoading: false,
  addDealerEventSuccess: false,
};

export const AppSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setUserId: (state, action: PayloadAction<string>) => {
      state.userId = action.payload
    },
    setIsStoreAdmin: (state, action: PayloadAction<boolean>) => {
      state.isStoreAdmin = action.payload;
    },
    setIsStoreAdminRole: (state, action: PayloadAction<boolean>) => {
      state.isStoreAdminRole = action.payload;
    },
    setIsGlobalAdmin: (state, action: PayloadAction<boolean>) => {
      state.isGlobalAdmin = action.payload;
    },
    setStoreDealerID: (state, action: PayloadAction<string>) => {
      state.storeDealerId = action.payload;
    },
    setIsGlobalOperationsAdmin: (state, action: PayloadAction<boolean>) => {
      state.isGlobalOperationsAdmin = action.payload;
    },
    setIsPreferredReportsRole: (state, action: PayloadAction<boolean>) => {
      state.isPreferredReportsRole = action.payload;
    },
    setIsSearchOnlyRole: (state, action: PayloadAction<boolean>) => {
      state.isSearchOnlyRole = action.payload;
    },
    setCurrentUserGroup: (state, action: PayloadAction<string>) => {
      state.currentUserGroup = action.payload;
    },
    setIsGlobalAdminRole: (state, action: PayloadAction<boolean>) => {
      state.isGlobalAdminRole = action.payload;
    },
    setIsGlobalOperationsAdminRole: (state, action: PayloadAction<boolean>) => {
      state.isGlobalOperationsAdminRole = action.payload;
    },
    setStoreId: (state, action: PayloadAction<string>) => {
      state.storeId = action.payload;
    },
    setCreateStoreData: (state, action: PayloadAction<StoreDetailsInterface>) => {
      state.createStoreData = action.payload;
    },
    setStoreConfiguration: (state, action: PayloadAction<fflSelectInterface>) => {
      state.fflSelectAPIKey = action.payload;
    },
    setDealerActionTimeout: (state, action: PayloadAction<DealerActionTimeout>) => {
      state.dealerActionTimeout = action.payload;
    },
    setStoreRefresh: (state, action: PayloadAction<boolean>) => {
      state.storeRefresh = action.payload;
    },
    setUserName: (state, action: PayloadAction<string>) => {
      state.userName = action.payload;
    },
    setIsProcessCompleted: (state,action: PayloadAction<boolean>) => {
      state.isProcessCompleted = action.payload;
    },
    callToGetCreateStoreApi: (
      state,
      action: PayloadAction<StoreDetailsInterface>
    ) => { },
    callToCreateStore: (
      state,
      action: PayloadAction<any>
    ) => { },
    callToGetStoreData: () => { },
    callToGetDealerAction: () => { },
    setStoreData: (state, action: PayloadAction<StoreDetailsInterface>) => {
      state.storeData = action.payload;
    },
    setIsCreateStoreLoading: (state, action: PayloadAction<boolean>) => {
      state.isCreateStoreLoading = action.payload;
    },
    setEmailError: (state, action: PayloadAction<string>) => {
      state.emailExistError = action.payload;
    },
    setDomainError: (state, action: PayloadAction<string>) => {
      state.domainExistError = action.payload;
    },
    setStoreLicenseError: (state, action: PayloadAction<string>) => {
      state.storeLicenseExistError = action.payload;
    },
    setIsImpersonating: (state, action: PayloadAction<boolean>) => {
      state.isImpersonating = action.payload;
    },
    callToImpersonateGlobalAdmin: (state, action: PayloadAction<{storeId: string; navigate: NavigateFunction}>) => {
    },
    callToStopImpersonateGlobalAdmin: (state, action: PayloadAction<{navigate: NavigateFunction}>) => {
    },
    setIsUserInfoLoaded: (state, action: PayloadAction<boolean>) => {
      state.isUserInfoLoaded = action.payload;
    },
    setIsMyFFLRole: (state, action: PayloadAction<boolean>) => {
      state.isMyFFLRole = action.payload;
    },
    setMyFFLDealerId: (state, action: PayloadAction<string>) => {
      state.myFFLDealerId = action.payload;
    },
    updateSOTLicensePath: (state, action: PayloadAction<{licenseKey:string; licensePath: string, source: string}>) => { },
    updateLOALicensePath: (state, action: PayloadAction<{licenseKey:string; licensePath: string; source: string}>) => { },
    setRefreshStatus: (state, action: { payload: boolean }) => {
      state.refreshStatus = action.payload;
    },
    setIsStoreAgent: (state, action: { payload: boolean }) => {
      state.isStoreAgent = action.payload;
    },
    callToGetStoreList: () => { },
    setStoreList: (state, action: { payload: any }) => {
      state.storeList = action.payload;
    },
    setUserEmail: (state, action: PayloadAction<string>) => {
      state.userEmail = action.payload;
    },
    setUserPermissions: (state, action: PayloadAction<any>) => {
      state.userPermissions = action.payload;
    },
    callToGetTrackingData: (state,
      action: PayloadAction<TrackingInterface>) => {state.trackingData = action.payload;

      },
    callToAddDealerEvent: (state, _action: PayloadAction<AddDealerEventPayload>) => { },
    setIsAddDealerEventLoading: (state, action: PayloadAction<boolean>) => {
      state.isAddDealerEventLoading = action.payload;
    },
    setAddDealerEventSuccess: (state, action: PayloadAction<boolean>) => {
      state.addDealerEventSuccess = action.payload;
    },
  },
});

export const {
  setIsStoreAdmin,
  setIsStoreAdminRole,
  setIsGlobalAdmin,
  setIsGlobalOperationsAdmin,
  setIsPreferredReportsRole,
  setIsSearchOnlyRole,
  setCurrentUserGroup,
  setIsGlobalAdminRole,
  setIsGlobalOperationsAdminRole,
  setStoreId,
  setCreateStoreData,
  setStoreRefresh,
  setUserName,
  setStoreConfiguration,
  setIsProcessCompleted,
  callToGetCreateStoreApi,
  callToGetStoreData,
  setStoreData,
  callToCreateStore,
  setIsCreateStoreLoading,
  setEmailError,
  setDomainError,
  setStoreLicenseError,
  setIsImpersonating,
  callToImpersonateGlobalAdmin,
  callToStopImpersonateGlobalAdmin,
  setDealerActionTimeout,
  callToGetDealerAction,
  setIsUserInfoLoaded,
  setIsMyFFLRole,
  setMyFFLDealerId,
  updateSOTLicensePath,
  updateLOALicensePath,
  setRefreshStatus,
  setIsStoreAgent,
  setUserId,
  callToGetStoreList,
  setStoreList,
  setUserEmail,
  setStoreDealerID,
  setUserPermissions,
  callToGetTrackingData,
  callToAddDealerEvent,
  setIsAddDealerEventLoading,
  setAddDealerEventSuccess,
} = AppSlice.actions;

export default AppSlice.reducer;
